import { distance } from "@turf/turf";
import { FruitSite, FruitTraceability, statementFruitSites } from "../../models/traceability/FruitTraceability";

/**
 * KPIs for the purchase report, derived client-side from the contracts already
 * fetched by GetTraceabilityData. No extra query, no period-over-period comparison.
 */
export type PurchaseKpis = {
    /** Sum of delivered quantity, in tonnes. */
    totalVolumeTons: number
    /** Quantity-weighted mean price, in Rp/kg. */
    averagePrice: number
    /** Mean mill-to-fruit-source distance in km, or undefined when not computable. */
    avgDeliveryRangeKm: number | undefined
    /** Distinct fruit sources delivered over the period. */
    totalFruitSources: number
    /** Number of contracts the KPIs are computed over. */
    contractCount: number
    /** The invoiced share of the above; undefined when no contract carries the `invoiced` flag. */
    invoiced: InvoicedKpis | undefined
}

/** Every contract is invoiceable, so these are a subset of the period totals. */
export type InvoicedKpis = {
    /** Sum of invoiced quantity, in tonnes. */
    volumeTons: number
    /** Invoiced volume over total volume, between 0 and 1. */
    volumeShare: number
    /** Quantity-weighted mean price of invoiced contracts, in Rp/kg. */
    averagePrice: number
    contractCount: number
}

const toNumber = (value: any): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/** A stable identity for a fruit site, so the same garden isn't counted twice. */
const fruitSiteKey = (site: FruitSite): string =>
    site.id !== undefined ? `site:${site.id}` : site.coordinates.join(',');

/** Total quantity (kg) and quantity-weighted mean price (Rp/kg) of a set of contracts. */
const volumeAndPrice = (contracts: FruitTraceability[]): { quantity: number, averagePrice: number } => {
    let quantity = 0;
    let weightedPriceSum = 0;
    contracts.forEach(contract => {
        const q = toNumber(contract.quantity);
        quantity += q;
        weightedPriceSum += toNumber(contract.price) * q;
    });
    return { quantity, averagePrice: quantity > 0 ? weightedPriceSum / quantity : 0 };
};

const isValidCoordinate = (coords: any): coords is number[] =>
    Array.isArray(coords) && coords.length === 2 && coords.every(c => Number.isFinite(Number(c)));

export const computePurchaseKpis = (
    contracts: FruitTraceability[],
    millCoordinates?: number[]
): PurchaseKpis => {
    const list = contracts ?? [];

    let distanceSum = 0;
    let distanceCount = 0;
    const sourceKeys = new Set<string>();

    const millCoords = isValidCoordinate(millCoordinates) ? millCoordinates : undefined;

    list.forEach(contract => {
        (contract.seller_statements ?? []).forEach((statement: any) => {
            const sites = statementFruitSites(statement);
            if (sites.length === 0 && statement?.statement_id !== undefined) {
                sourceKeys.add(`statement:${statement.statement_id}`);
            }

            sites.forEach(site => {
                sourceKeys.add(fruitSiteKey(site));
                if (millCoords) {
                    distanceSum += distance(millCoords, site.coordinates, { units: 'kilometers' });
                    distanceCount += 1;
                }
            });
        });
    });

    const total = volumeAndPrice(list);

    // The flag is not deployed everywhere yet: without it, show no invoiced figures
    // rather than a misleading "0 invoiced".
    const hasInvoicedFlag = list.some(contract => typeof contract.invoiced === 'boolean');
    const invoicedContracts = list.filter(contract => contract.isInvoiced());
    const invoiced = volumeAndPrice(invoicedContracts);

    return {
        totalVolumeTons: total.quantity / 1000,
        averagePrice: total.averagePrice,
        avgDeliveryRangeKm: distanceCount > 0 ? distanceSum / distanceCount : undefined,
        totalFruitSources: sourceKeys.size,
        contractCount: list.length,
        invoiced: hasInvoicedFlag
            ? {
                volumeTons: invoiced.quantity / 1000,
                volumeShare: total.quantity > 0 ? invoiced.quantity / total.quantity : 0,
                averagePrice: invoiced.averagePrice,
                contractCount: invoicedContracts.length,
            }
            : undefined,
    };
};
